import { Inject, Injectable, NgZone } from '@angular/core';
import { toError } from 'src/app/common/logging/error-normalization';

import makeDebug from '../../../../makeDebug';
import { AccountService } from '../../services/account.service';
import { ConnectionStateService } from '../../services/connection-state.service';
import { IFeathersService } from '../../services/contracts/sync/feathers-service';
import { ISyncService } from '../../services/contracts/sync/service/sync-service';
import { FeathersService } from '../../services/feathers.service';
import { SyncTimeoutRepeaterServiceFactory } from '../../services/sync-timeout-repeater-factory..service';
import { MaintenanceSyncService } from '../../services/sync/maintenance-sync.service';
import { ISyncContext } from '../contracts/sync/sync-context';
import { ISyncElement, SyncElementResult } from '../contracts/sync/sync-element';
import { MaintenanceSynchroniser as Synchroniser } from '../contracts/sync/sync-endpoints';

const debug = makeDebug('sync:maintenance-synchroniser');

@Injectable()
export class MaintenanceSynchroniser implements ISyncElement {
  public get name(): string {
    return Synchroniser;
  }
  private _isRunning = false;

  constructor(
    @Inject(MaintenanceSyncService) private _maintenanceSyncService: ISyncService,
    private _ngZone: NgZone,
    @Inject(FeathersService) private _feathersService: IFeathersService,
    private _accountService: AccountService,
    private _syncTimeoutRepeaterServiceFactory: SyncTimeoutRepeaterServiceFactory,
    private _connectionStateService: ConnectionStateService
  ) {}

  public async execute(context: ISyncContext): Promise<SyncElementResult> {
    debug('entered execute function', context.params.isOnline);
    const result: SyncElementResult = { contextId: context.id, jobDone: false };
    if (!this._connectionStateService.isSocketConnected || this._isRunning) {
      result.connected = false;
      return result;
    }
    result.connected = true;

    this._isRunning = true;

    try {
      await this._accountService.accountReady;
      await this._maintenanceSyncService.setup(this._feathersService);
      debug('calling sync of sync-timeout-repeater', context.params.maintenanceToken);

      const syncTimeoutRepeater = this._syncTimeoutRepeaterServiceFactory.create(
        this._maintenanceSyncService,
        this._ngZone,
        null
      );

      const account = await this._accountService.getAccount();
      // This promise lives on the cancellation token: an unhandled rejection here
      // used to surface much later, misattributed to the TokenHandler step and
      // without any content (work item 27022). Log it at its origin instead.
      context.params.maintenanceToken.promise = syncTimeoutRepeater
        .sync(context.params.maintenanceToken, account)
        .catch(error => window.logger.error('Maintenance sync failed', toError(error, 'Maintenance sync failed')));

      result.jobDone = true;
    } catch (error) {
      // a throw here must not end the pipeline run; the maintenance sync is optional for it
      window.logger.error(
        'MaintenanceSynchroniser failed to start maintenance sync',
        toError(error, 'MaintenanceSynchroniser failed to start maintenance sync')
      );
    } finally {
      this._isRunning = false;
    }

    return result;
  }
}
